"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const LAYERS = [
    {
        key: "ctrl",
        nameKey: "Mvc_sort_ctrl",
        icon: "routing-bold-duotone",
        tile: "bg-amber-500/15 text-amber-300",
    },
    {
        key: "model",
        nameKey: "Mvc_sort_model",
        icon: "database-bold-duotone",
        tile: "bg-sky-500/15 text-sky-300",
    },
    {
        key: "view",
        nameKey: "Mvc_sort_view",
        icon: "window-frame-bold-duotone",
        tile: "bg-emerald-500/15 text-emerald-300",
    },
];

const NAME_KEY = {
    ctrl: "Mvc_sort_ctrl",
    model: "Mvc_sort_model",
    view: "Mvc_sort_view",
};

// Ordre fixe (pas d'aléatoire : rendu stable), 2 par couche.
const ITEMS = [
    { textKey: "Mvc_sort_i1", layer: "model" },
    { textKey: "Mvc_sort_i2", layer: "view" },
    { textKey: "Mvc_sort_i3", layer: "ctrl" },
    { textKey: "Mvc_sort_i4", layer: "model" },
    { textKey: "Mvc_sort_i5", layer: "view" },
    { textKey: "Mvc_sort_i6", layer: "ctrl" },
];

export default function MvcSorter() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [index, setIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [picked, setPicked] = useState(null); // couche choisie (en attente d'avance)
    const timer = useRef(null);

    useEffect(() => {
        return () => {
            if (timer.current) clearTimeout(timer.current);
        };
    }, []);

    const done = index >= ITEMS.length;
    const current = done ? null : ITEMS[index];
    const answered = picked !== null;
    const correct = answered && picked === current.layer;

    function choose(layerKey) {
        if (answered || done) return;
        const ok = layerKey === current.layer;
        setPicked(layerKey);
        if (ok) setScore((s) => s + 1);
        timer.current = setTimeout(
            () => {
                setPicked(null);
                setIndex((i) => i + 1);
            },
            reduce ? 450 : 1050,
        );
    }

    function replay() {
        if (timer.current) clearTimeout(timer.current);
        setIndex(0);
        setScore(0);
        setPicked(null);
    }

    return (
        <section id="mvc-sorter" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Mvc_sort_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Mvc_sort_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-slate-400/15 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <SolarIcon
                            name="layers-minimalistic-bold-duotone"
                            className="w-3.5 h-3.5 text-slate-300"
                        />
                        mvc
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Mvc_sort_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-8">
                    {!done ? (
                        <>
                            {/* Progression + score */}
                            <div className="flex items-center justify-between mb-5">
                                <span className="text-[12px] font-medium text-gray-400">
                                    {t("Mvc_sort_progress", {
                                        n: index + 1,
                                        total: ITEMS.length,
                                    })}
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-300">
                                    <SolarIcon
                                        name="check-circle-bold"
                                        className="w-4 h-4"
                                    />
                                    {score}
                                </span>
                            </div>

                            {/* Carte responsabilité */}
                            <div className="text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300/80 mb-3">
                                {t("Mvc_sort_prompt")}
                            </div>
                            <div className="max-w-xl mx-auto">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={index}
                                        initial={
                                            reduce ? false : { opacity: 0, y: 10 }
                                        }
                                        animate={
                                            answered && !correct && !reduce
                                                ? { opacity: 1, y: 0, x: [0, -8, 8, -5, 5, 0] }
                                                : { opacity: 1, y: 0 }
                                        }
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.3 }}
                                        className={`rounded-2xl px-5 py-6 text-center ring-1 transition-colors duration-300 ${
                                            answered
                                                ? correct
                                                    ? "bg-emerald-500/10 ring-emerald-400/40"
                                                    : "bg-rose-500/10 ring-rose-400/40"
                                                : "bg-white/[0.04] ring-white/10"
                                        }`}
                                    >
                                        <SolarIcon
                                            name="code-square-bold"
                                            className="w-6 h-6 text-slate-300 mx-auto mb-3"
                                        />
                                        <p className="text-base sm:text-lg font-medium text-white leading-snug mb-0">
                                            {t(current.textKey)}
                                        </p>
                                    </motion.div>
                                </AnimatePresence>

                                {/* Boutons couches */}
                                <div className="mt-5 grid grid-cols-3 gap-2.5">
                                    {LAYERS.map((layer) => {
                                        const isChosen = picked === layer.key;
                                        const isAnswer =
                                            current.layer === layer.key;
                                        let state =
                                            "bg-white/[0.04] ring-white/10 hover:bg-white/[0.08]";
                                        if (answered) {
                                            if (isChosen && correct)
                                                state =
                                                    "bg-emerald-500/15 ring-emerald-400/60 ring-2";
                                            else if (isChosen && !correct)
                                                state =
                                                    "bg-rose-500/15 ring-rose-400/60 ring-2";
                                            else if (isAnswer)
                                                state =
                                                    "bg-emerald-500/10 ring-emerald-400/50 ring-2";
                                            else state = "bg-white/[0.02] ring-white/10 opacity-50";
                                        }
                                        return (
                                            <button
                                                key={layer.key}
                                                type="button"
                                                onClick={() => choose(layer.key)}
                                                disabled={answered}
                                                className={`flex flex-col items-center gap-2 rounded-xl px-2 py-3.5 ring-1 transition active:scale-95 disabled:cursor-default ${state}`}
                                            >
                                                <span
                                                    className={`flex items-center justify-center w-10 h-10 rounded-xl ${layer.tile}`}
                                                >
                                                    <SolarIcon
                                                        name={layer.icon}
                                                        className="w-5 h-5"
                                                    />
                                                </span>
                                                <span className="text-[13px] font-semibold text-white">
                                                    {t(layer.nameKey)}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Feedback */}
                                <div className="h-6 mt-3 text-center">
                                    <AnimatePresence mode="wait">
                                        {answered && (
                                            <motion.p
                                                key={correct ? "ok" : "ko"}
                                                initial={
                                                    reduce
                                                        ? false
                                                        : { opacity: 0, y: 4 }
                                                }
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className={`text-[13px] font-semibold mb-0 ${
                                                    correct
                                                        ? "text-emerald-300"
                                                        : "text-rose-300"
                                                }`}
                                            >
                                                {correct
                                                    ? t("Mvc_sort_correct")
                                                    : t("Mvc_sort_wrong", {
                                                          layer: t(
                                                              NAME_KEY[
                                                                  current.layer
                                                              ],
                                                          ),
                                                      })}
                                            </motion.p>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </>
                    ) : (
                        /* Écran de fin */
                        <motion.div
                            initial={reduce ? false : { opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ type: "spring", stiffness: 220, damping: 20 }}
                            className="max-w-md mx-auto text-center py-4"
                        >
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/40 mb-4">
                                <SolarIcon
                                    name="cup-star-bold-duotone"
                                    className="w-8 h-8 text-emerald-300"
                                />
                            </div>
                            <div className="text-lg font-bold text-white">
                                {t("Mvc_sort_done_title")}
                            </div>
                            <p className="mt-1 text-sm text-gray-400 mb-4">
                                {t("Mvc_sort_done_sub", {
                                    score,
                                    total: ITEMS.length,
                                })}
                            </p>
                            <button
                                type="button"
                                onClick={replay}
                                className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-4 py-2 text-[13px] text-gray-200 transition active:scale-95"
                            >
                                <SolarIcon
                                    name="restart-bold-duotone"
                                    className="w-4 h-4"
                                />
                                {t("Mvc_sort_replay")}
                            </button>
                        </motion.div>
                    )}
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Mvc_sort_caption")}
            </p>
        </section>
    );
}
